import { useState, useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useHotkeys } from 'react-hotkeys-hook';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { apiClient } from '../api/client';
import { GradingStatus } from '../api/types';
import type { ImageCopy, PreviewDescriptor } from '../api/types';
import { useImagePreloader } from '../hooks/useImagePreloader';
import { useImageZoom } from '../hooks/useImageZoom';
import { useAsyncImage } from '../hooks/useAsyncImage';
import { ensurePreviewReady } from '../hooks/previewPoll';
import UndoRedoToolbar from './UndoRedoToolbar';
import AstrometryPanel from './AstrometryPanel';
import AstrometryOverlay from './AstrometryOverlay';
import SatellitePanel from './SatellitePanel';
import SatelliteTrackOverlay from './SatelliteTrackOverlay';
import ImageFileLocation from './ImageFileLocation';
import TiltInspector from './TiltInspector';
import { useScopedQuality } from '../hooks/useSequenceAnalysis';
import QualityAnalysisSummary from './QualityAnalysisSummary';
import ScoringChangedNote from './ScoringChangedNote';
import QualityRegionOverlay from './QualityRegionOverlay';
import CalibrationPanel from './CalibrationPanel';
import DetailSection from './DetailSection';
import {
  colorPreviewEnabled,
  setColorPreview,
  useColorPreview,
} from '../hooks/useColorPreview';
import { setSkyOverlay, useSkyOverlay } from '../hooks/useSkyOverlay';
import { useDetailSection } from '../hooks/useDetailSections';
import { useSidebarScrollAnchor } from '../hooks/useSidebarScrollAnchor';
import { useAccess } from '../auth/access';

interface ImageDetailViewProps {
  dbId: string;
  imageId: number;
  projectId?: number | null;
  targetId?: number | null;
  qualityFilterName?: string;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onGrade: (status: 'accepted' | 'rejected' | 'pending', shiftHeld?: boolean) => void;
  adjacentImageIds?: { next: number[]; previous: number[] };
  // Optional grading system for undo/redo (passed from parent)
  grading?: {
    canWrite: boolean;
    canUndo: boolean;
    canRedo: boolean;
    isLoading: boolean;
    undoStackSize: number;
    redoStackSize: number;
    undo: () => Promise<boolean>;
    redo: () => Promise<boolean>;
    getLastAction: () => any; // eslint-disable-line @typescript-eslint/no-explicit-any
    getNextRedoAction: () => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  };
}

/** The frame's size in pixels from its header, when the catalog has it. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function headerDimensions(metadata: Record<string, any> | undefined) {
  const width = metadata?.ImageWidth || metadata?.NAXIS1 || metadata?.ImageSize?.[0];
  const height = metadata?.ImageHeight || metadata?.NAXIS2 || metadata?.ImageSize?.[1];
  return typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0
    ? { width, height }
    : null;
}

export default function ImageDetailView({
  dbId,
  imageId,
  projectId,
  targetId,
  qualityFilterName,
  onClose,
  onNext,
  onPrevious,
  onGrade,
  adjacentImageIds,
  grading,
}: ImageDetailViewProps) {
  const queryClient = useQueryClient();
  const { canCompute } = useAccess();
  const [showStars, setShowStars] = useState(false);
  const [showTilt, setShowTilt] = useState(false);
  // Shared with the grid and the overview, so a frame does not change
  // appearance on the way into the detail view. On by default; off gives the
  // luminance rendition the grader measures, and a mono frame looks the same
  // either way.
  const showColor = useColorPreview();
  const [showPsf, setShowPsf] = useState(false);
  const [psfImageLoading, setPsfImageLoading] = useState(false);
  const [maxStars] = useState(1000);
  const [imageSize, setImageSize] = useState<'screen' | 'large' | 'original'>('large');
  const [isOriginalLoaded, setIsOriginalLoaded] = useState(false);
  const [useOriginalImage, setUseOriginalImage] = useState(false);
  const [imageError, setImageError] = useState(false);
  // The viewer's remembered choice; stars and PSF take the image over while
  // they are on, and the overlay comes back with them off.
  const skyOverlay = useSkyOverlay();
  const showAstrometry = skyOverlay && !showStars && !showPsf;
  const toggleSkyOverlay = () => {
    // Toggle what is on screen: with stars or PSF hiding a remembered "on",
    // pressing O asks to see the sky.
    setSkyOverlay(!showAstrometry);
    setShowStars(false);
    setShowPsf(false);
  };
  const [showQualityRegions, setShowQualityRegions] = useState(false);
  const [showSatellites, setShowSatellites] = useState(false);
  const [displayedBitmapDimensions, setDisplayedBitmapDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  // On a phone the image area takes the frame's shape: `.detail-image` in
  // App.css reads --detail-frame-aspect. It comes from the bitmap the zoom is
  // calibrated against, else the frame's header, else the last frame shown,
  // so the area keeps its size from one frame to the next.
  const detailImageRef = useRef<HTMLDivElement>(null);
  const [bitmapAspect, setBitmapAspect] = useState<{ imageId: number; aspect: number } | null>(null);

  // The sidebar's panels fold away, and stay as the viewer left them from
  // image to image. Calibration keeps its own rule (see CalibrationPanel).
  // A folded panel's data still loads where the image overlays need it.
  const infoSection = useDetailSection('info');
  const qualitySection = useDetailSection('quality');
  const skySection = useDetailSection('sky');
  const satelliteSection = useDetailSection('satellites');
  const [sidebar, setSidebar] = useState<HTMLDivElement | null>(null);
  useSidebarScrollAnchor(sidebar);

  // Fetch image details
  const { data: image, isLoading } = useQuery({
    queryKey: ['db', dbId, 'image', imageId],
    queryFn: () => apiClient.getImage(dbId, imageId),
    placeholderData: (previousData) => previousData, // Keep showing previous image while loading new one
  });

  // Which file to show: the light's own, or one of the calibrated or
  // registered copies other software wrote. Held by name, so stepping
  // through frames keeps showing the same kind of copy where one exists.
  const [copyLabel, setCopyLabel] = useState<string | null>(null);
  const copies: ImageCopy[] = image?.id === imageId ? image.copies ?? [] : [];
  const ownCopy = copies.find((candidate) => candidate.primary);
  const viewCopies = copies.filter(
    (candidate) => !candidate.primary && candidate.available !== false
  );
  const selectedCopy = copyLabel
    ? viewCopies.find((candidate) => candidate.label === copyLabel)
    : undefined;
  const copy = selectedCopy?.uuid;
  // Sky, satellite and quality overlays are drawn on the light's own pixel
  // grid; a registered copy has been resampled onto another one.
  const geometryMatches = selectedCopy?.kind !== 'registered';

  // State machine to prevent feedback loops
  const imageStateRef = useRef<'large' | 'switching-to-original' | 'original'>('large');
  // Guard: request original-resolution generation at most once per image.
  const originalRequestedRef = useRef(false);
  const mainImageKey = `${dbId}:${imageId}:${showStars ? 'stars' : 'preview'}:${maxStars}:${
    showColor ? 'color' : 'mono'
  }:${copy ?? 'own'}`;
  const mainImageKeyRef = useRef(mainImageKey);
  mainImageKeyRef.current = mainImageKey;

  // 'fit': the view follows the image (refit whenever a new one loads).
  // 'user': the user owns the view — zoom percent and position are preserved
  // exactly across arrow-key navigation and preview↔original bitmap swaps,
  // until an explicit Fit/reset returns to 'fit'.
  const viewModeRef = useRef<'fit' | 'user'>('fit');

  // Initialize zoom functionality
  const zoom = useImageZoom({
    minScale: 0.1,
    maxScale: 10.0,
    onViewModeChange: (mode) => {
      viewModeRef.current = mode;
    },
  });

  // Check if image has overflow (is larger than container)
  const hasOverflow = zoom.zoomState.scale > 1 || 
    (zoom.containerRef.current && zoom.imageRef.current && 
     zoom.imageRef.current.naturalWidth && zoom.imageRef.current.naturalHeight &&
     (zoom.imageRef.current.naturalWidth * zoom.zoomState.scale > zoom.containerRef.current.clientWidth ||
      zoom.imageRef.current.naturalHeight * zoom.zoomState.scale > zoom.containerRef.current.clientHeight));

  // Preload adjacent images for smooth navigation
  const nextImageIds = adjacentImageIds ? 
    [...adjacentImageIds.next, ...adjacentImageIds.previous] : [];
  
  useImagePreloader(dbId, imageId, nextImageIds, {
    preloadCount: 2,
    includeAnnotated: showStars,
    includeStarData: showStars,
    imageSize: imageSize,
  });

  // Async loading for the main preview/annotated image (PSF has its own
  // endpoint and is not queued here). On a cache miss the server returns 202
  // and this drives a "Generating…" indicator, then reloads when ready.
  const mainSize: 'large' | 'original' = useOriginalImage ? 'original' : 'large';
  // The star overlay is drawn in colour, so it stays on a greyscale frame:
  // markers over a colour image compete with the pixels they annotate.
  const colorPreview = showColor && !showStars;
  const largeNonPsfSrc = showStars
    ? apiClient.getAnnotatedUrl(dbId, imageId, 'large', maxStars, copy)
    : apiClient.getPreviewUrl(dbId, imageId, { size: 'large', color: colorPreview, copy });
  const originalNonPsfSrc = showStars
    ? apiClient.getAnnotatedUrl(dbId, imageId, 'original', maxStars, copy)
    : apiClient.getPreviewUrl(dbId, imageId, { size: 'original', color: colorPreview, copy });
  const nonPsfSrc = mainSize === 'original' ? originalNonPsfSrc : largeNonPsfSrc;
  const nonPsfDescriptor: PreviewDescriptor = showStars
    ? { imageId, kind: 'annotated', size: mainSize, maxStars, copy }
    : { imageId, kind: 'preview', size: mainSize, color: colorPreview, copy };
  const asyncImg = useAsyncImage(dbId, nonPsfSrc, nonPsfDescriptor);
  // `src` is what the <img> renders (may carry a `v=` cache-buster after a
  // generation-triggered reload); `baseSrc` is the stable identity used to
  // decide whether the visible artifact still belongs to this image/size.
  const [visibleMainImage, setVisibleMainImage] = useState<{
    key: string;
    src: string;
    baseSrc: string;
    loadedSrc: string | null;
  }>(() => ({
    key: mainImageKey,
    src: nonPsfSrc,
    baseSrc: nonPsfSrc,
    loadedSrc: null,
  }));
  const visibleMainSrc =
    visibleMainImage.key === mainImageKey ? visibleMainImage.src : nonPsfSrc;
  const visibleMainBaseSrc =
    visibleMainImage.key === mainImageKey ? visibleMainImage.baseSrc : nonPsfSrc;
  const loadedMainSrc =
    visibleMainImage.key === mainImageKey ? visibleMainImage.loadedSrc : null;
  const currentMainSrcIsLoaded =
    visibleMainSrc === loadedMainSrc;
  const currentNonPsfSources = [largeNonPsfSrc, originalNonPsfSrc];
  const visibleMainSrcIsCurrent =
    currentNonPsfSources.includes(visibleMainBaseSrc);


  const qualityScope = useScopedQuality(dbId, projectId, targetId, qualityFilterName);
  const quality = qualityScope.qualityByImage.get(imageId);
  const qualityStatus = qualityScope.isLoading
      ? 'Loading the sequence quality score and reason…'
      : qualityScope.error
        ? 'Could not load the sequence score for this image.'
        : quality
          ? undefined
          : 'No sequence score is available for this image.';
  const qualityStatusSummary = qualityScope.isLoading
      ? 'loading…'
      : qualityScope.error
        ? 'unavailable'
        : 'no score';

  const {
    data: astrometry,
    isLoading: astrometryLoading,
    error: astrometryError,
  } = useQuery({
    queryKey: ['db', dbId, 'image', imageId, 'astrometry'],
    queryFn: () => apiClient.getImageAstrometry(dbId, imageId),
    retry: false,
  });

  const solveAstrometry = useMutation({
    mutationFn: (scope: { dbId: string; imageId: number }) =>
      apiClient.solveImageAstrometry(scope.dbId, scope.imageId),
    onSuccess: (analysis, scope) => {
      queryClient.setQueryData(
        ['db', scope.dbId, 'image', scope.imageId, 'astrometry'],
        analysis
      );
      // The viewer asked for this solve, so show what it found, and keep
      // showing it: that is a choice like pressing the toggle.
      if (analysis.solution && scope.dbId === dbId && scope.imageId === imageId) {
        setSkyOverlay(true);
        setShowStars(false);
        setShowPsf(false);
      }
    },
  });
  const solveMatchesCurrent = solveAstrometry.variables?.dbId === dbId
    && solveAstrometry.variables?.imageId === imageId;

  const {
    data: satelliteStatus,
    isLoading: satellitesLoading,
    error: satellitesError,
  } = useQuery({
    queryKey: ['db', dbId, 'image', imageId, 'satellites'],
    queryFn: () => apiClient.getImageSatellites(dbId, imageId),
    retry: false,
  });

  const predictSatellites = useMutation({
    mutationFn: (scope: { dbId: string; imageId: number }) =>
      apiClient.predictImageSatellites(scope.dbId, scope.imageId),
    onSuccess: (analysis, scope) => {
      queryClient.setQueryData(
        ['db', scope.dbId, 'image', scope.imageId, 'satellites'],
        { analysis, orbital_elements_cached: true }
      );
      void queryClient.invalidateQueries({
        queryKey: ['db', scope.dbId, 'image', scope.imageId, 'astrometry'],
      });
      if (scope.dbId === dbId && scope.imageId === imageId) {
        setShowSatellites(true);
        setShowStars(false);
        setShowPsf(false);
      }
    },
  });
  const satellitePredictionMatchesCurrent = predictSatellites.variables?.dbId === dbId
    && predictSatellites.variables?.imageId === imageId;

  // Fetch star detection
  const { data: starData, isLoading: starDataLoading } = useQuery({
    queryKey: ['db', dbId, 'stars', imageId, copy ?? 'own'],
    queryFn: () => apiClient.getStarDetection(dbId, imageId, copy),
    enabled: showStars,
  });


  // Keyboard shortcuts
  useHotkeys('escape', onClose, [onClose]);
  useHotkeys('k,right', () => {
    onNext(); // K/Right: the next image in the order the grid shows
  }, { enableOnFormTags: true }, [onNext]);
  useHotkeys('j,left', () => {
    onPrevious(); // J/Left: the previous one
  }, { enableOnFormTags: true }, [onPrevious]);
  useHotkeys('a,shift+a', (event) => onGrade('accepted', event.shiftKey), [onGrade]);
  useHotkeys('x,shift+x', (event) => onGrade('rejected', event.shiftKey), [onGrade]);
  useHotkeys('u,shift+u', (event) => onGrade('pending', event.shiftKey), [onGrade]);
  useHotkeys('i', () => setShowTilt(current => !current), []);
  // V steps through the light's own file and its copies.
  useHotkeys('v', () => {
    if (viewCopies.length === 0) return;
    const labels = [null, ...viewCopies.map((candidate) => candidate.label)];
    const index = labels.indexOf(selectedCopy?.label ?? null);
    setCopyLabel(labels[(index + 1) % labels.length]);
  }, [viewCopies, selectedCopy]);
  useHotkeys('s', () => {
    setShowStars(s => !s);
    setShowPsf(false); // Turn off PSF when showing stars
    setShowSatellites(false);
  }, [showStars]);
  // Colour is a view of the same exposure, not a different analysis, so it
  // does not turn off the overlays the way stars and PSF turn off each other.
  useHotkeys('c', () => setColorPreview(!colorPreviewEnabled()), []);
  useHotkeys('p', () => {
    const newPsfState = !showPsf;
    setShowPsf(newPsfState);
    setShowStars(false); // Turn off stars when showing PSF
    setShowSatellites(false);
    if (newPsfState) {
      setPsfImageLoading(true);
    }
  }, [showPsf]);
  useHotkeys('o', () => {
    if (astrometry?.solution) {
      toggleSkyOverlay();
    } else if (canCompute && !(solveMatchesCurrent && solveAstrometry.isPending)) {
      solveAstrometry.mutate({ dbId, imageId });
    }
  }, [astrometry?.solution, canCompute, solveAstrometry.isPending, solveMatchesCurrent, showAstrometry]);
  useHotkeys('t', () => {
    if (satelliteStatus?.analysis) {
      setShowSatellites((visible) => !visible);
      setShowStars(false);
      setShowPsf(false);
    } else if (canCompute && !(satellitePredictionMatchesCurrent && predictSatellites.isPending)) {
      predictSatellites.mutate({ dbId, imageId });
    }
  }, [
    canCompute,
    satelliteStatus?.analysis,
    satellitePredictionMatchesCurrent,
    predictSatellites.isPending,
  ]);
  useHotkeys('z', () => setImageSize(s => s === 'screen' ? 'large' : 'screen'), []);
  useHotkeys('plus,equal', () => zoom.zoomIn(), [zoom.zoomIn]);
  useHotkeys('minus', () => zoom.zoomOut(), [zoom.zoomOut]);
  useHotkeys('0', () => zoom.resetZoom(), [zoom.resetZoom]);
  useHotkeys('1', () => zoom.zoomTo100(), [zoom.zoomTo100]);
  useHotkeys('f', () => zoom.zoomToFit(), [zoom.zoomToFit]);

  // Load original dimensions from metadata if available
  useEffect(() => {
    const dimensions = headerDimensions(image?.metadata);
    if (dimensions) {
      zoom.setImageDimensions(dimensions.width, dimensions.height, true);
    }
  }, [image, zoom]);
  
  // Combined effect for image preloading and switching. Triggers on the RAW
  // scale of the displayed bitmap: scale > 1 means the current bitmap is
  // being magnified past its native pixels (blurry) and the original-
  // resolution artifact is needed, whichever preview size is showing.
  useEffect(() => {
    if (!image || showPsf) return;

    const scale = zoom.zoomState.scale;
    const state = imageStateRef.current;

    // Preload near 1:1. Route through the interactive queue so an uncached
    // 'original' is actually generated (its 202 is not a failure); only flip
    // to it once generation completes.
    if (scale > 0.8 && state === 'large' && !isOriginalLoaded && !originalRequestedRef.current) {
      originalRequestedRef.current = true;
      const originalUrl = showStars
        ? apiClient.getAnnotatedUrl(dbId, imageId, 'original', maxStars)
        : apiClient.getPreviewUrl(dbId, imageId, { size: 'original', color: colorPreview });
      const originalDescriptor: PreviewDescriptor = showStars
        ? { imageId, kind: 'annotated', size: 'original', maxStars }
        : { imageId, kind: 'preview', size: 'original', color: colorPreview };

      const requestedKey = mainImageKey;
      void ensurePreviewReady(dbId, originalUrl, originalDescriptor).then((ok) => {
        // With zoom persisting across navigation these requests fire on every
        // image; ignore resolutions that arrive after we moved on.
        if (mainImageKeyRef.current !== requestedKey) return;
        if (ok) {
          setIsOriginalLoaded(true);
        } else {
          // Transient generation failure — clear the guard so the next zoom
          // change retries instead of leaving this image stuck on the preview.
          originalRequestedRef.current = false;
        }
      });
    }

    // State machine transitions - only switch to original, never back
    if (state === 'large' && scale >= 1.0 && isOriginalLoaded) {
      // Switch to original
      imageStateRef.current = 'switching-to-original';
      setUseOriginalImage(true);
    }
    // Never switch back from original to large
  }, [
    zoom,
    imageId,
    showStars,
    showPsf,
    image,
    isOriginalLoaded,
    dbId,
    maxStars,
    mainImageKey,
    colorPreview,
  ]);
  
  // Reset preload state when image changes. Deliberately does NOT touch the
  // zoom state: in 'user' view mode the zoom/pan carries over to the next
  // image (reportBitmapLoaded reconciles it against the incoming bitmap).
  useLayoutEffect(() => {
    setIsOriginalLoaded(false);
    setUseOriginalImage(false);
    originalRequestedRef.current = false;
    setVisibleMainImage({
      key: mainImageKey,
      src: largeNonPsfSrc,
      baseSrc: largeNonPsfSrc,
      loadedSrc: null,
    });
    imageStateRef.current = 'large';
    setImageError(false);
    setDisplayedBitmapDimensions(null);
  }, [dbId, imageId, showStars, maxStars, mainImageKey, largeNonPsfSrc]);

  // A bitmap finished loading into one of the <img> elements. Hand its
  // dimensions to the zoom hook: fit when the view is in 'fit' mode, keep the
  // view (exactly, or remapped across a size change) when the user owns it or
  // when this load is the large→original swap.
  const reportBitmapLoaded = (img: HTMLImageElement) => {
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (!width || !height) return;

    setDisplayedBitmapDimensions({ width, height });

    // Give the phone's image area this frame's shape before the fit below
    // measures it; the state keeps the next render in step.
    const aspect = width / height;
    detailImageRef.current?.style.setProperty('--detail-frame-aspect', String(aspect));
    setBitmapAspect({ imageId, aspect });

    zoom.setImageDimensions(width, height, useOriginalImage);

    const switching = imageStateRef.current === 'switching-to-original';
    zoom.applyBitmapDimensions(
      width,
      height,
      switching || viewModeRef.current === 'user' ? 'preserve' : 'fit'
    );
    if (switching) {
      imageStateRef.current = 'original';
    }
  };

  // Show loading state only on initial load
  if (!image && isLoading) {
    return (
      <div className="image-detail-overlay">
        <div className="image-detail">
          <div className="detail-loading">
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  // If no image data at all, close the modal
  if (!image) {
    onClose();
    return null;
  }

  const getStatusClass = () => {
    switch (image.grading_status) {
      case GradingStatus.Accepted:
        return 'status-accepted';
      case GradingStatus.Rejected:
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleString();
  };

  // A folded Image Information panel: why the light was rejected, when it
  // was taken, for how long, and its stars and background.
  const metadata = image.metadata ?? {};
  const exposure = metadata.Exposure ?? metadata.ExposureDuration;
  const infoSummary = [
    image.reject_reason ? `rejected: ${image.reject_reason}` : null,
    image.acquired_date
      ? new Date(image.acquired_date * 1000).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })
      : null,
    exposure !== undefined && exposure !== null ? `${exposure}s` : null,
    typeof metadata.HFR === 'number' ? `HFR ${metadata.HFR.toFixed(2)}` : null,
    typeof metadata.DetectedStars === 'number' ? `${metadata.DetectedStars} stars` : null,
    typeof metadata.Mean === 'number' ? `mean ${metadata.Mean.toFixed(0)}` : null,
  ].filter(Boolean).join(' · ');

  // Hide until the visible src has actually loaded AND still belongs to this
  // image/size — never show a stale or errored <img> (e.g. one whose original
  // request 202'd while its regenerated copy loads under a `v=` buster).
  const hideMainImage =
    !showPsf && (!visibleMainSrcIsCurrent || !currentMainSrcIsLoaded);

  const header = image.id === imageId ? headerDimensions(image.metadata) : null;
  const frameAspect =
    (bitmapAspect?.imageId === imageId ? bitmapAspect.aspect : null)
    ?? (header ? header.width / header.height : null)
    ?? bitmapAspect?.aspect
    ?? null;

  return (
    <div className="image-detail-overlay" onClick={onClose}>
      <div className="image-detail" onClick={e => e.stopPropagation()}>
        <div className="detail-header">
          <h2>{image.target_name} - {image.filter_name || 'No filter'}</h2>
          <div className={`status-banner ${getStatusClass()}`}>
            {image.grading_status === GradingStatus.Accepted && '✓ ACCEPTED'}
            {image.grading_status === GradingStatus.Rejected && '✗ REJECTED'}
            {image.grading_status === GradingStatus.Pending && '○ PENDING'}
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="detail-content">
          <div
            className="detail-image"
            ref={detailImageRef}
            style={frameAspect ? { '--detail-frame-aspect': String(frameAspect) } as CSSProperties : undefined}
          >
            <div 
              className={`image-container zoom-container ${hasOverflow ? 'has-overflow' : ''}`}
              ref={zoom.containerRef}
              onWheel={zoom.handleWheel}
              onMouseDown={zoom.handleMouseDown}
              onMouseMove={zoom.handleMouseMove}
              onMouseUp={zoom.handleMouseUp}
              onMouseLeave={zoom.handleMouseUp}
              onTouchStart={zoom.handleTouchStart}
              onTouchMove={zoom.handleTouchMove}
              onTouchEnd={zoom.handleTouchEnd}
              onTouchCancel={zoom.handleTouchEnd}
              tabIndex={0}
              onKeyDown={zoom.handleKeyDown}
            >
              {/* Loading overlay for star detection and PSF views */}
              {(showStars && starDataLoading) || (showPsf && psfImageLoading) ? (
                <div className="image-loading-overlay">
                  <div className="loading-content">
                    <div className="loading-spinner"></div>
                    <span className="loading-text">
                      {showStars && starDataLoading ? 'Loading star detection...' : 'Loading PSF analysis...'}
                    </span>
                  </div>
                </div>
              ) : null}
              {imageError || (!showPsf && asyncImg.state === 'error') ? (
                <div className="detail-image-error" style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '500px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    textAlign: 'center',
                    color: 'var(--color-text-muted)'
                  }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '16px' }}>
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                    </svg>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', fontWeight: 'normal' }}>Image not available</h3>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-hint)' }}>{asyncImg.error ?? 'The requested image could not be found on the server'}</p>
                  </div>
                </div>
              ) : (
                <>
                {!showPsf && visibleMainSrc !== asyncImg.src && asyncImg.state !== 'error' && (
                  <img
                    className="detail-image-preload"
                    src={asyncImg.src}
                    alt=""
                    aria-hidden="true"
                    loading="eager"
                    onError={asyncImg.onError}
                    onLoad={(e) => {
                      asyncImg.onLoad();
                      reportBitmapLoaded(e.currentTarget);
                      setVisibleMainImage({
                        key: mainImageKey,
                        src: asyncImg.src,
                        baseSrc: nonPsfSrc,
                        loadedSrc: asyncImg.src,
                      });
                    }}
                    draggable={false}
                  />
                )}
                <img
                  ref={zoom.imageRef}
                  key={`${imageId}-${showStars ? 'stars' : showPsf ? 'psf' : 'normal'}`}
                  className={[
                    'detail-main-image',
                    hideMainImage ? 'detail-image-hidden' : null,
                  ].filter(Boolean).join(' ')}
                  src={
                    showPsf
                      ? apiClient.getPsfUrl(dbId, imageId, {
                          num_stars: 9,
                          psf_type: 'moffat',
                          sort_by: 'r2',
                          selection: 'top-n',
                          copy,
                        })
                      : visibleMainSrc
                  }
                  alt={`${image.target_name} - ${image.filter_name || 'No filter'}`}
                  style={{
                    transform: `translate(${zoom.zoomState.offsetX}px, ${zoom.zoomState.offsetY}px) scale(${zoom.zoomState.scale})`,
                    cursor: zoom.zoomState.scale > 1 ? 'grab' : 'default',
                    transformOrigin: '0 0',
                  }}
                  onError={
                    showPsf
                      ? () => setImageError(true)
                      : visibleMainSrc === asyncImg.src
                        ? asyncImg.onError
                        : undefined
                  }
                  onLoad={(e) => {
                    // The PSF diagnostic image never feeds the zoom
                    // calibration — its dimensions are unrelated to the
                    // preview/original bitmaps.
                    if (showPsf) {
                      setPsfImageLoading(false);
                      return;
                    }

                    if (visibleMainSrc === asyncImg.src) {
                      asyncImg.onLoad();
                    }
                    if (!visibleMainSrcIsCurrent) {
                      return;
                    }

                    reportBitmapLoaded(e.currentTarget);
                    setVisibleMainImage((current) =>
                      current.key === mainImageKey && current.src === visibleMainSrc
                        ? { ...current, loadedSrc: visibleMainSrc }
                        : current
                    );
                  }}
                  draggable={false}
                />
                {!showPsf &&
                  showAstrometry &&
                  geometryMatches &&
                  !hideMainImage &&
                  displayedBitmapDimensions &&
                  astrometry?.solution && (
                    <AstrometryOverlay
                      solution={astrometry.solution}
                      density={0.2}
                      className="detail-astrometry-overlay"
                      data-testid="astrometry-overlay"
                      style={{
                        width: `${displayedBitmapDimensions.width}px`,
                        height: `${displayedBitmapDimensions.height}px`,
                        transform: `translate(${zoom.zoomState.offsetX}px, ${zoom.zoomState.offsetY}px) scale(${zoom.zoomState.scale})`,
                        transformOrigin: '0 0',
                      }}
                    />
                  )}
                {!showPsf &&
                  showSatellites &&
                  geometryMatches &&
                  !hideMainImage &&
                  displayedBitmapDimensions &&
                  satelliteStatus?.analysis && (
                    <SatelliteTrackOverlay
                      analysis={satelliteStatus.analysis}
                      imageWidth={satelliteStatus.analysis.image_width}
                      imageHeight={satelliteStatus.analysis.image_height}
                      className="detail-satellite-overlay"
                      style={{
                        width: `${displayedBitmapDimensions.width}px`,
                        height: `${displayedBitmapDimensions.height}px`,
                        transform: `translate(${zoom.zoomState.offsetX}px, ${zoom.zoomState.offsetY}px) scale(${zoom.zoomState.scale})`,
                        transformOrigin: '0 0',
                      }}
                    />
                  )}
                {!showPsf &&
                  showQualityRegions &&
                  geometryMatches &&
                  !hideMainImage &&
                  displayedBitmapDimensions &&
                  quality?.spatial_overlay && (
                    <QualityRegionOverlay
                      overlay={quality.spatial_overlay}
                      className="detail-quality-region-overlay"
                      style={{
                        width: `${displayedBitmapDimensions.width}px`,
                        height: `${displayedBitmapDimensions.height}px`,
                        transform: `translate(${zoom.zoomState.offsetX}px, ${zoom.zoomState.offsetY}px) scale(${zoom.zoomState.scale})`,
                        transformOrigin: '0 0',
                      }}
                    />
                  )}
                </>
              )}
              {!showPsf &&
                (asyncImg.state === 'generating' || asyncImg.state === 'loading') && (
                  <div className="image-loading-overlay">
                    <div className="loading-content">
                      <div className="loading-spinner"></div>
                      <span className="loading-text">
                        {asyncImg.state === 'generating'
                          ? 'Generating preview…'
                          : 'Loading…'}
                      </span>
                    </div>
                  </div>
                )}
            </div>
          </div>

          <div className="detail-info" ref={setSidebar}>
            <DetailSection
              title="Image Information"
              summary={infoSummary}
              open={infoSection.open}
              onToggle={infoSection.toggle}
              data-testid="image-info-panel"
            >
              {/* Date on its own row */}
              <div className="date-row">
                <span className="date-label">Date:</span>
                <span className="date-value">{formatDate(image.acquired_date)}</span>
              </div>
              
              {/* Camera on its own row */}
              {image.metadata?.Camera !== undefined && (
                <div className="date-row">
                  <span className="date-label">Camera:</span>
                  <span className="date-value">{image.metadata.Camera}</span>
                </div>
              )}
              
              {/* Two-column layout for other metadata */}
              <dl>
                {starData && (
                  <>
                    <dt>Stars:</dt>
                    <dd>{starData.detected_stars}</dd>
                    <dt>Avg HFR:</dt>
                    <dd>{starData.average_hfr.toFixed(2)}</dd>
                    <dt>Avg FWHM:</dt>
                    <dd>{starData.average_fwhm.toFixed(2)}</dd>
                  </>
                )}
                
                {image.metadata?.Min !== undefined && (
                  <>
                    <dt>Min:</dt>
                    <dd>{typeof image.metadata.Min === 'number' ? image.metadata.Min.toFixed(0) : image.metadata.Min}</dd>
                  </>
                )}
                
                {image.metadata?.Mean !== undefined && (
                  <>
                    <dt>Mean:</dt>
                    <dd>{typeof image.metadata.Mean === 'number' ? image.metadata.Mean.toFixed(1) : image.metadata.Mean}</dd>
                  </>
                )}
                
                {image.metadata?.Median !== undefined && (
                  <>
                    <dt>Median:</dt>
                    <dd>{typeof image.metadata.Median === 'number' ? image.metadata.Median.toFixed(1) : image.metadata.Median}</dd>
                  </>
                )}
                
                {image.metadata?.HFR !== undefined && (
                  <>
                    <dt>HFR:</dt>
                    <dd>{typeof image.metadata.HFR === 'number' ? image.metadata.HFR.toFixed(2) : image.metadata.HFR}</dd>
                  </>
                )}
                
                {image.metadata?.DetectedStars !== undefined && (
                  <>
                    <dt>Det. Stars:</dt>
                    <dd>{image.metadata.DetectedStars}</dd>
                  </>
                )}
                
                {image.metadata?.Exposure !== undefined && (
                  <>
                    <dt>Exposure:</dt>
                    <dd>{image.metadata.Exposure}s</dd>
                  </>
                )}
                
                {image.metadata?.Temperature !== undefined && (
                  <>
                    <dt>Temp:</dt>
                    <dd>{image.metadata.Temperature}°C</dd>
                  </>
                )}
                
                {image.metadata?.Gain !== undefined && (
                  <>
                    <dt>Gain:</dt>
                    <dd>{image.metadata.Gain}</dd>
                  </>
                )}
              </dl>

              <ImageFileLocation
                dbId={dbId}
                filesystemPath={image.filesystem_path}
                catalogPath={
                  typeof image.metadata?.FileName === 'string'
                    ? image.metadata.FileName
                    : null
                }
              />
              
              {image.reject_reason && (
                <div className="reject-reason">
                  <strong>Reject Reason:</strong>
                  <p>{image.reject_reason}</p>
                </div>
              )}
            </DetailSection>

            {viewCopies.length > 0 && (
              <div className="detail-copy-switch" role="group" aria-label="File shown">
                {[
                  { label: null as string | null, text: ownCopy?.label ?? 'Raw' },
                  ...viewCopies.map((candidate) => ({ label: candidate.label, text: candidate.label })),
                ].map((option) => {
                  const active = (selectedCopy?.label ?? null) === option.label;
                  return (
                    <button
                      key={option.label ?? 'own'}
                      type="button"
                      className={`zoom-btn-compact${active ? ' active' : ''}`}
                      aria-pressed={active}
                      onClick={() => setCopyLabel(option.label)}
                      title={
                        option.label === null
                          ? 'The light\'s own file'
                          : viewCopies.find((candidate) => candidate.label === option.label)?.file_name
                      }
                    >
                      {option.text}
                    </button>
                  );
                })}
                {!geometryMatches && (
                  <small className="detail-copy-note">
                    Registered onto another frame: sky, satellite and quality overlays are hidden.
                  </small>
                )}
              </div>
            )}

            {/* The score and reason follow scoring set in Sequence. */}
            <ScoringChangedNote />

            <QualityAnalysisSummary
              quality={quality}
              statusMessage={qualityStatus}
              statusSummary={qualityStatusSummary}
              open={qualitySection.open}
              onToggleOpen={qualitySection.toggle}
              overlayVisible={showQualityRegions && !!quality?.spatial_overlay}
              onToggleOverlay={quality?.spatial_overlay ? () => {
                setShowQualityRegions((visible) => !visible);
                setShowStars(false);
                setShowPsf(false);
              } : undefined}
            />

            <CalibrationPanel dbId={dbId} imageId={imageId} projectId={projectId ?? null} />

            <AstrometryPanel
              analysis={astrometry}
              isLoading={astrometryLoading}
              error={astrometryError instanceof Error ? astrometryError.message : undefined}
              solveError={solveMatchesCurrent && solveAstrometry.error instanceof Error
                ? solveAstrometry.error.message
                : undefined}
              isSolving={solveMatchesCurrent && solveAstrometry.isPending}
              canSolve={canCompute}
              open={skySection.open}
              onToggleOpen={skySection.toggle}
              overlayVisible={showAstrometry && !!astrometry?.solution}
              onToggleOverlay={toggleSkyOverlay}
              onSolve={() => solveAstrometry.mutate({ dbId, imageId })}
            />

            <SatellitePanel
              status={satelliteStatus}
              isLoading={satellitesLoading}
              error={satellitesError instanceof Error ? satellitesError.message : undefined}
              predictError={satellitePredictionMatchesCurrent && predictSatellites.error instanceof Error
                ? predictSatellites.error.message
                : undefined}
              isPredicting={satellitePredictionMatchesCurrent && predictSatellites.isPending}
              canPredict={canCompute}
              open={satelliteSection.open}
              onToggleOpen={satelliteSection.toggle}
              overlayVisible={showSatellites && !!satelliteStatus?.analysis}
              onToggleOverlay={() => {
                setShowSatellites((visible) => !visible);
                setShowStars(false);
                setShowPsf(false);
              }}
              onPredict={() => predictSatellites.mutate({ dbId, imageId })}
            />

            <div className="detail-actions">
              <button 
                className="action-button accept" 
                onClick={(event) => onGrade('accepted', event.shiftKey)}
                disabled={grading ? !grading.canWrite : false}
              >
                Accept (A)
              </button>
              <button 
                className="action-button reject" 
                onClick={(event) => onGrade('rejected', event.shiftKey)}
                disabled={grading ? !grading.canWrite : false}
              >
                Reject (X)
              </button>
              <button 
                className="action-button pending" 
                onClick={(event) => onGrade('pending', event.shiftKey)}
                disabled={grading ? !grading.canWrite : false}
              >
                Unmark (U)
              </button>
            </div>

            {/* Undo/Redo Toolbar (only show if grading system is provided) */}
            {grading && (
              <UndoRedoToolbar
                canUndo={grading.canUndo}
                canRedo={grading.canRedo}
                isProcessing={grading.isLoading}
                undoStackSize={grading.undoStackSize}
                redoStackSize={grading.redoStackSize}
                onUndo={grading.undo}
                onRedo={grading.redo}
                getLastAction={grading.getLastAction}
                getNextRedoAction={grading.getNextRedoAction}
                className="compact"
              />
            )}

            <div className="detail-shortcuts">
              <div className="shortcut-grid">
                <span>K/→ Next</span>
                <span>J/← Prev</span>
                <span>A Accept</span>
                <span>X Reject</span>
                <span>U Pending</span>
                <span>S Stars {showStars ? '✓' : ''}</span>
                <span>I Tilt {showTilt ? '✓' : ''}</span>
                <span>P PSF {showPsf ? '✓' : ''}</span>
                <span>O Sky {showAstrometry && astrometry?.solution ? '✓' : ''}</span>
                {viewCopies.length > 0 && <span>V View</span>}
                <span>Z Size</span>
                {grading && <span>⌘Z Undo</span>}
                {grading && <span>⌘Y Redo</span>}
              </div>
            </div>

            {/* Compact Zoom Controls at Bottom */}
            <div className="zoom-section-bottom">
              <div className="zoom-info-compact">
                <span className="zoom-percentage-compact">{zoom.getZoomPercentage()}%</span>
              </div>
              <div className="zoom-buttons-compact">
                <button 
                  className="zoom-btn-compact" 
                  onClick={zoom.zoomOut}
                  title="Zoom Out (-)"
                >
                  -
                </button>
                <button 
                  className="zoom-btn-compact" 
                  onClick={zoom.zoomToFit}
                  title="Fit to Screen (F)"
                >
                  Fit
                </button>
                <button 
                  className="zoom-btn-compact" 
                  onClick={zoom.zoomTo100}
                  title="100% (1)"
                >
                  100%
                </button>
                <button 
                  className="zoom-btn-compact" 
                  onClick={zoom.zoomIn}
                  title="Zoom In (+)"
                >
                  +
                </button>
                <button
                  className={`zoom-btn-compact${showColor ? ' active' : ''}`}
                  onClick={() => setColorPreview(!showColor)}
                  aria-pressed={showColor}
                  disabled={showStars}
                  title={
                    showStars
                      ? 'Star markers are drawn over a greyscale frame'
                      : 'One-shot-color rendition (C) — off shows the luminance the grader measures'
                  }
                >
                  Color
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* A phone grades from a bar under the panels, in reach of a thumb
            however far they are scrolled; a wider screen hides it and grades
            from the panel and the keys. */}
        <div className="detail-phone-bar" role="group" aria-label="Grade and step">
          <button type="button" className="detail-phone-step" aria-label="Previous image" onClick={onPrevious}>
            <ChevronLeft size={20} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="action-button accept"
            onClick={() => onGrade('accepted', false)}
            disabled={grading ? !grading.canWrite : false}
          >
            Accept
          </button>
          <button
            type="button"
            className="action-button reject"
            onClick={() => onGrade('rejected', false)}
            disabled={grading ? !grading.canWrite : false}
          >
            Reject
          </button>
          <button
            type="button"
            className="action-button pending"
            onClick={() => onGrade('pending', false)}
            disabled={grading ? !grading.canWrite : false}
          >
            Unmark
          </button>
          <button type="button" className="detail-phone-step" aria-label="Next image" onClick={onNext}>
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        </div>
      </div>
      <TiltInspector
        open={showTilt}
        dbId={dbId}
        imageId={imageId}
        onClose={() => setShowTilt(false)}
      />
    </div>
  );
}
